import { Component, Input, OnInit } from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Property} from "../../model/property";
import {ObjectOwner} from "../../model/object-owner";
import {ObjectOwnerType} from "../../model/enums/object-owner-type";
import FormUtils from "../../utils/form-utils";
import {CustomValidators} from "../../custom-validators/custom-validators";

@Component({
  selector: 'app-object-owner',
  templateUrl: './object-owner.component.html',
  styleUrls: ['./object-owner.component.css']
})
export class ObjectOwnerComponent implements OnInit {

  @Input() property: Property;

  objectOwnerFormArray: FormArray;
  objectOwnerFormGroup: FormGroup;
  maxBirthDate: string = `${new Date().getFullYear()}-12-31`

  protected readonly ObjectOwnerType = ObjectOwnerType;
  protected readonly FormUtils = FormUtils;

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    this.initializeObjectOwnerForm();
  }

  /**
   * Initializes the form array for multiple object owners.
   */
  public initializeObjectOwnerForm(): void {
    if (!this.property.objectOwnerList?.length) {
      this.property.objectOwnerList = [new ObjectOwner()];
    }

    const objectOwnerGroups = this.property.objectOwnerList.map(owner =>
        this.buildObjectOwnerGroup(owner)
    );

    this.objectOwnerFormArray = this.fb.array(objectOwnerGroups);
    this.objectOwnerFormGroup = this.fb.group({
      objectOwners: this.objectOwnerFormArray
    });
  }

  /**
   * Returns the form array of object owners.
   */
  get objectOwners(): FormArray {
    return this.objectOwnerFormGroup.get('objectOwners') as FormArray;
  }

  /**
   * Updates the object owner type when the user switches radio buttons.
   * @param index Index of the object owner in the form array
   * @param newType Selected object owner type
   */
  public onObjectOwnerTypeChange(index: number, newType: ObjectOwnerType): void {
    this.objectOwners.at(index).patchValue({ objectOwnerType: newType });
  }

  /**
   * Adds a new object owner entry to the form array.
   */
  public addObjectOwner(): void {
    const newOwner = new ObjectOwner();
    const group = this.buildObjectOwnerGroup(newOwner);
    this.objectOwners.push(group);
  }

  /**
   * Removes the object owner at the given index from the form array.
   * @param index Index of the object owner to remove
   */
  public removeObjectOwner(index: number): void {
    this.objectOwners.removeAt(index);
  }

  /**
   * Returns true if all object owner entries in the form are valid.
   */
  public isObjectOwnerFormValid(): boolean {
    const isFormValid = this.objectOwnerFormGroup.valid;
    if(!isFormValid){
      FormUtils.markInvalidInputs(this.objectOwnerFormGroup);
    }
    return isFormValid;
  }

  /**
   * Builds and returns a FormGroup configured for an object owner.
   *
   * @param {ObjectOwner} owner - The object owner data used to populate the form group.
   * @return {FormGroup} A FormGroup instance pre-filled with the given owner data and applied validators.
   */
  private buildObjectOwnerGroup(owner: ObjectOwner): FormGroup {
    const group = this.fb.group({
      firstname: [owner.firstname || '', Validators.required],
      lastname: [owner.lastname || '', Validators.required],
      birthName: [owner.birthName || ''],
      birthDate: [owner.birthDate || null],
      company: [owner.company || ''],
      objectOwnerType: [owner.objectOwnerType || ObjectOwnerType.COMPANY, Validators.required]
    });

    this.updateValidatorsForType(group); // apply initial validation
    this.subscribeToTypeChanges(group);

    return group;
  }

  /**
   * Subscribes to changes in the 'objectOwnerType' field of the provided FormGroup.
   * When a change is detected, it updates the validators for the type.
   *
   * @param {FormGroup} group - The FormGroup whose 'objectOwnerType' field will be monitored for changes.
   */
  private subscribeToTypeChanges(group: FormGroup): void {
    group.get('objectOwnerType')?.valueChanges.subscribe(() => {
      this.updateValidatorsForType(group);
    });
  }

  /**
   * Updates validators dynamically based on the type of object owner.
   * Ensures that specific validation rules are applied to form controls
   * depending on whether the owner type is PRIVATE or not.
   *
   * @param group The FormGroup containing the controls to update validators for.
   */
  private updateValidatorsForType(group: FormGroup): void {
    const type = group.get('objectOwnerType')?.value;

    const firstname = group.get('firstname');
    const lastname = group.get('lastname');
    const birthDate = group.get('birthDate');
    const company = group.get('company');

    firstname?.setValidators([Validators.required]);
    lastname?.setValidators([Validators.required]);

    if (type === ObjectOwnerType.PRIVATE) {
      birthDate?.setValidators([Validators.required, CustomValidators.minDate("1900-01-01"), CustomValidators.maxDate(this.maxBirthDate)]);
      company?.clearValidators();
    } else {
      birthDate?.clearValidators();
      company?.setValidators([Validators.required]);
    }

    // Update validity for all involved fields
    [firstname, lastname, birthDate, company].forEach(control => control?.updateValueAndValidity());
  }

  /**
   * Determines whether the given control's owner type is private.
   *
   * @param {AbstractControl} owner The control containing the object owner type to evaluate.
   * @return {boolean} True if the owner's type is private, otherwise false.
   */
  public isPrivate(owner: AbstractControl): boolean {
    return owner.get('objectOwnerType')?.value === ObjectOwnerType.PRIVATE;
  }

  /**
   * Checks if the provided owner has the type `ObjectOwnerType.COMPANY`.
   *
   * @param {AbstractControl} owner - The control containing the owner type information.
   * @return {boolean} True if the owner type is `ObjectOwnerType.COMPANY`, otherwise false.
   */
  public isCompany(owner: AbstractControl): boolean {
    return owner.get('objectOwnerType')?.value === ObjectOwnerType.COMPANY;
  }

}
